// src/components/ServicesTypeScroll.js
// Loop Reservas — mobile-safe category carousel v4.
// This component owns the category carousel layout so MainMenu legacy CSS cannot turn it into a grid.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ImageWithFallback from "./ImageWithFallback";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import loopCategoryBeauty from "../assets/images/categories/loop-category-beauty.png";
import loopCategoryWellness from "../assets/images/categories/loop-category-wellness.png";
import loopCategoryEvents from "../assets/images/categories/loop-category-events.png";
import loopCategoryFitness from "../assets/images/categories/loop-category-fitness.png";
import loopCategoryMore from "../assets/images/categories/loop-category-more.png";

const PUBLIC_NO_IMAGE = (process.env.PUBLIC_URL || "") + "/no-image.svg";

function normalizeName(s) {
  if (!s) return "";
  return String(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/gi, "_")
    .toLowerCase();
}

function loadServiceImages() {
  const map = {};
  try {
    // eslint-disable-next-line global-require
    const ctx = require.context("../assets/images/categories", false, /\.(png|jpe?g|svg|webp)$/);
    ctx.keys().forEach((k) => {
      const fileName = k.replace("./", "");
      const key = normalizeName(fileName.replace(/\.(png|jpe?g|svg|webp)$/i, ""));
      const resolved = ctx(k);
      map[key] = resolved && resolved.default ? resolved.default : resolved;
    });
    return map;
  } catch (e) {}

  try {
    if (typeof import.meta !== "undefined" && typeof import.meta.globEager === "function") {
      const modules = import.meta.globEager("/src/assets/images/categories/*.{png,jpg,jpeg,svg,webp}");
      Object.keys(modules).forEach((p) => {
        const seg = p.split("/").pop();
        const key = normalizeName(seg.replace(/\.(png|jpe?g|svg|webp)$/i, ""));
        const mod = modules[p];
        map[key] = mod && mod.default ? mod.default : mod;
      });
      return map;
    }
  } catch (e) {}

  return map;
}

const imagesMap = loadServiceImages();

const LOOP_CATEGORY_IMAGE_BY_KEY = {
  belleza_barberias: loopCategoryBeauty,
  belleza_y_barberias: loopCategoryBeauty,
  barberias: loopCategoryBeauty,
  belleza: loopCategoryBeauty,
  bienestar_spa: loopCategoryWellness,
  bienestar_y_spa: loopCategoryWellness,
  bienestar: loopCategoryWellness,
  spa: loopCategoryWellness,
  eventos: loopCategoryEvents,
  evento: loopCategoryEvents,
  entretenimiento_fitness: loopCategoryFitness,
  entretenimiento_y_fitness: loopCategoryFitness,
  fitness: loopCategoryFitness,
  entretenimiento: loopCategoryFitness,
  otros: loopCategoryMore,
  mas_categorias: loopCategoryMore,
};

const LOOP_CATEGORY_IMAGE_BY_INDEX = [
  loopCategoryBeauty,
  loopCategoryWellness,
  loopCategoryEvents,
  loopCategoryFitness,
  loopCategoryMore,
];


const LOOP_P6_CATEGORY_ICON_FILES = {
  belleza_barberias: "loop-cat-belleza",
  belleza_y_barberias: "loop-cat-belleza",
  barberias: "loop-cat-belleza",
  belleza: "loop-cat-belleza",
  bienestar_spa: "loop-cat-bienestar",
  bienestar_y_spa: "loop-cat-bienestar",
  bienestar: "loop-cat-bienestar",
  spa: "loop-cat-bienestar",
  eventos: "loop-cat-eventos",
  evento: "loop-cat-eventos",
  entretenimiento_fitness: "loop-cat-fitness",
  entretenimiento_y_fitness: "loop-cat-fitness",
  fitness: "loop-cat-fitness",
  entretenimiento: "loop-cat-fitness",
  otros: "loop-cat-otros",
  mas_categorias: "loop-cat-otros",
};

function getLoopP6CategoryIcon(item, idx) {
  const label = normalizeName(item?.name || item?.label || item?.id || "");
  const directCategoryImage = LOOP_CATEGORY_IMAGE_BY_KEY[label] || LOOP_CATEGORY_IMAGE_BY_INDEX[idx];

  if (directCategoryImage) return directCategoryImage;

  return null;
}



function isBrowserWeb() {
  try {
    return typeof window !== "undefined" && window.location && (window.location.protocol === "http:" || window.location.protocol === "https:");
  } catch (e) {
    return false;
  }
}

function buildRuntimeVariants(resolved) {
  if (!resolved) return [PUBLIC_NO_IMAGE];

  const r = String(resolved);
  const out = [r];

  if (isBrowserWeb() && r.startsWith("/")) {
    try {
      out.push(window.location.origin + r);
    } catch (e) {}
  }

  try {
    const pub = process.env.PUBLIC_URL || "";
    if (pub) {
      const cleanPub = pub.endsWith("/") ? pub.slice(0, -1) : pub;
      const stripped = r.replace(/^\/+/, "");
      out.push(`${cleanPub}/${stripped}`);
    }
  } catch (e) {}

  if (!isBrowserWeb()) {
    const stripped = r.replace(/^\/+/, "");

    try {
      if (typeof window !== "undefined" && window.location && window.location.protocol === "file:") {
        const href = String(window.location.href || "");
        const idx = href.lastIndexOf("/");
        const base = idx >= 0 ? href.slice(0, idx + 1) : href;
        out.push(base + stripped);
      }
    } catch (e) {}

    out.push("file:///android_asset/public/" + stripped);
    out.push("file:///android_asset/www/" + stripped);
    out.push(stripped);
  }

  if (!out.includes(PUBLIC_NO_IMAGE)) out.push(PUBLIC_NO_IMAGE);
  return Array.from(new Set(out.filter(Boolean)));
}

function tryFindImageResolved(item, idx) {
  if (!item) return null;

  const loopP6Icon = getLoopP6CategoryIcon(item, idx);
  if (loopP6Icon) return loopP6Icon;

  if (item.icon) return item.icon;
  if (item.imageUrl) return item.imageUrl;

  if (item.imageName) {
    const k = normalizeName(item.imageName);
    if (imagesMap[k]) return imagesMap[k];
    return (process.env.PUBLIC_URL || "") + "/assets/images/categories/" + item.imageName;
  }

  const raw = item.name || item.id || `service_${idx + 1}`;
  const norm = normalizeName(raw);

  if (imagesMap[norm]) return imagesMap[norm];

  const byIdxKey = `servicio${idx + 1}`;
  if (imagesMap[byIdxKey]) return imagesMap[byIdxKey];

  const foundKey = Object.keys(imagesMap).find((k) => k && (k.includes(norm) || norm.includes(k)));
  if (foundKey) return imagesMap[foundKey];

  const vals = Object.values(imagesMap || {});
  if (vals && vals.length) return vals[idx % vals.length];

  return PUBLIC_NO_IMAGE;
}

function getUniqueItems(list) {
  const seen = new Set();
  const out = [];

  (Array.isArray(list) ? list : []).forEach((item, idx) => {
    if (!item) return;

    const label = item.name || item.id || `cat_${idx}`;
    const key = item.id ? `id:${item.id}` : `name:${normalizeName(label)}`;
    if (seen.has(key)) return;

    seen.add(key);
    out.push(item);
  });

  return out;
}

export default function ServicesTypeScroll({
  businessTypes = [],
  recommendedBusinesses = [],
  db = null,
  onSelect = () => {},
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSample, setShowSample] = useState(false);

  const [mobileSlideIndex, setMobileSlideIndex] = useState(0);
  const swipeRef = useRef({ active: false, startX: 0, startY: 0 });

  const mobileVisibleCount = 5;
  const maxMobileSlideIndex = Math.max(0, (Array.isArray(items) && items.length ? items.length : 5) - mobileVisibleCount);

  const clampMobileSlide = useCallback((next) => {
    const max = Math.max(0, maxMobileSlideIndex);
    return Math.max(0, Math.min(max, next));
  }, [maxMobileSlideIndex]);

  const moveMobileSlide = useCallback((dir) => {
    setMobileSlideIndex((prev) => clampMobileSlide(prev + dir));
  }, [clampMobileSlide]);

  const handleCarouselPointerDown = useCallback((event) => {
    swipeRef.current = {
      active: true,
      startX: event.clientX || 0,
      startY: event.clientY || 0,
    };
  }, []);

  const handleCarouselPointerUp = useCallback((event) => {
    const swipe = swipeRef.current;
    if (!swipe.active) return;

    const endX = event.clientX || 0;
    const endY = event.clientY || 0;
    const dx = endX - swipe.startX;
    const dy = endY - swipe.startY;

    swipeRef.current.active = false;

    if (Math.abs(dx) < 28 || Math.abs(dx) < Math.abs(dy) * 1.15) return;

    if (dx < 0) moveMobileSlide(1);
    else moveMobileSlide(-1);
  }, [moveMobileSlide]);

  const handleCarouselPointerCancel = useCallback(() => {
    swipeRef.current.active = false;
  }, []);

  const isMobileApp =
    typeof document !== "undefined" &&
    document.body &&
    document.body.classList &&
    document.body.classList.contains("is-mobile-app");

  useEffect(() => {
    let mounted = true;

    async function decide() {
      setLoading(true);

      try {
        const fromProps = getUniqueItems(businessTypes);
        if (fromProps.length > 0) {
          if (mounted) {
            setItems(fromProps);
            setLoading(false);
          }
          return;
        }

        if (Array.isArray(recommendedBusinesses) && recommendedBusinesses.length > 0) {
          const seen = new Set();
          const out = [];

          recommendedBusinesses.forEach((b) => {
            const t = b && (b.type || b.category || b.businessType || b.serviceType || b.service);
            if (!t) return;

            const key = String(t).trim();
            if (!key || seen.has(key)) return;

            seen.add(key);
            out.push({ id: key, name: key, icon: b.icon || "" });
          });

          if (out.length > 0) {
            if (mounted) {
              setItems(out);
              setLoading(false);
            }
            return;
          }
        }

        if (db) {
          try {
            const col = collection(db, "businessTypes");
            const q = query(col, where("active", "==", true), orderBy("priority", "asc"));
            const snap = await getDocs(q);

            if (snap && !snap.empty) {
              const arr = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
              if (mounted) {
                setItems(getUniqueItems(arr));
                setLoading(false);
              }
              return;
            }
          } catch (e) {
            console.warn("[ServicesTypeScroll] firestore fetch failed:", e && e.message ? e.message : e);
          }
        }

        if (mounted) {
          setItems([]);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setItems([]);
          setLoading(false);
        }
      }
    }

    decide();

    return () => {
      mounted = false;
    };
  }, [businessTypes, recommendedBusinesses, db]);

  useEffect(() => {
    if (isMobileApp && !showSample && (!items || items.length === 0) && !loading) {
      const t = setTimeout(() => setShowSample(true), 120);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isMobileApp, items, loading, showSample]);

  const sample = [
    { id: "s1", name: "Belleza & Barberías" },
    { id: "s2", name: "Bienestar & Spa" },
    { id: "s3", name: "Eventos" },
    { id: "s4", name: "Entretenimiento & Fitness" },
    { id: "s5", name: "Otros" },
  ];

  const toRender = items && items.length > 0 ? items : showSample ? sample : [];
  if (!toRender || toRender.length === 0) return null;

  const effectiveMaxMobileSlideIndex = Math.max(0, toRender.length - mobileVisibleCount);

  if (mobileSlideIndex > effectiveMaxMobileSlideIndex) {
    setMobileSlideIndex(effectiveMaxMobileSlideIndex);
  }

  const css = `
    /* ServicesTypeScroll clean v2
       Desktop stays visually stable.
       Mobile uses a controlled carousel, not native overflow. */

    #loop-services-section .loop-categories-visual-wrap > .loop-category-carousel-v4-root {
      display: block !important;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
      box-sizing: border-box !important;
      position: relative !important;
    }

    #loop-services-section .loop-category-carousel-v4 {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      align-items: flex-start !important;
      justify-content: flex-start !important;
      gap: clamp(18px, 2vw, 32px) !important;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 2px 0 4px !important;
      box-sizing: border-box !important;
      overflow: visible !important;
      transform: none !important;
      background: transparent !important;
    }

    #loop-services-section .loop-category-carousel-v4-card {
      appearance: none !important;
      -webkit-appearance: none !important;
      border: 0 !important;
      outline: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      transform: none !important;
      flex: 0 0 138px !important;
      width: 138px !important;
      min-width: 138px !important;
      max-width: 138px !important;
      min-height: 150px !important;
      margin: 0 !important;
      padding: 10px 10px 12px !important;
      border-radius: 26px !important;
      overflow: hidden !important;
      box-sizing: border-box !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: flex-start !important;
      gap: 13px !important;
      text-align: center !important;
      cursor: pointer !important;
      -webkit-tap-highlight-color: transparent !important;
    }

    #loop-services-section .loop-category-carousel-v4-card:hover {
      transform: translateY(-3px) !important;
      background: rgba(240, 253, 250, 0.46) !important;
      box-shadow: 0 16px 34px rgba(15, 23, 42, 0.045) !important;
      filter: none !important;
    }

    #loop-services-section .loop-category-carousel-v4-image {
      width: 108px !important;
      height: 108px !important;
      min-width: 108px !important;
      min-height: 108px !important;
      max-width: 108px !important;
      max-height: 108px !important;
      flex: 0 0 108px !important;
      margin: 0 auto !important;
      padding: 0 !important;
      border: 0 !important;
      border-radius: 26px !important;
      overflow: hidden !important;
      background: rgba(224, 242, 241, 0.92) !important;
      box-shadow: 0 14px 28px rgba(15, 23, 42, 0.055) !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      box-sizing: border-box !important;
    }

    #loop-services-section .loop-category-carousel-v4-img {
      width: 100% !important;
      height: 100% !important;
      max-width: 108px !important;
      max-height: 108px !important;
      object-fit: contain !important;
      object-position: center !important;
      display: block !important;
      margin: 0 !important;
      pointer-events: none !important;
      user-select: none !important;
    }

    #loop-services-section .loop-category-carousel-v4-label {
      width: 100% !important;
      max-width: 68px !important;
      margin: 0 auto !important;
      color: #0f172a !important;
      font-size: 9.4px !important;
      line-height: 1.15 !important;
      font-weight: 900 !important;
      text-align: center !important;
      white-space: normal !important;
      overflow: hidden !important;
      overflow-wrap: anywhere !important;
      display: -webkit-box !important;
      -webkit-line-clamp: 2 !important;
      -webkit-box-orient: vertical !important;
      pointer-events: none !important;
      user-select: none !important;
    }

    .loop-category-carousel-v4-mobile-window,
    .loop-category-carousel-v4-mobile-controls {
      display: contents;
    }

    @media (max-width: 899px) {
      #loop-services-section .loop-category-carousel-v4-root {
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
      }

      .loop-category-carousel-v4-mobile-window {
        display: block !important;
        width: 100% !important;
        max-width: 100% !important;
        overflow: hidden !important;
        touch-action: pan-y !important;
        user-select: none !important;
        cursor: grab !important;
        padding: 2px 0 2px !important;
      }

      .loop-category-carousel-v4-mobile-window:active {
        cursor: grabbing !important;
      }

      #loop-services-section .loop-category-carousel-v4 {
        gap: 13px !important;
        width: max-content !important;
        max-width: none !important;
        min-width: max-content !important;
        overflow: visible !important;
        padding: 2px 0 4px !important;
        transition: transform 220ms cubic-bezier(.2,.8,.2,1) !important;
        transform: translateX(0) !important;
        will-change: transform !important;
      }

      #loop-services-section .loop-category-carousel-v4-card {
        flex: 0 0 60px !important;
        width: 60px !important;
        min-width: 60px !important;
        max-width: 60px !important;
        min-height: 78px !important;
        padding: 0 !important;
        border-radius: 18px !important;
        gap: 6px !important;
      }

      #loop-services-section .loop-category-carousel-v4-card:hover {
        transform: none !important;
        background: transparent !important;
        box-shadow: none !important;
      }

      #loop-services-section .loop-category-carousel-v4-image {
        width: 76px !important;
        height: 76px !important;
        min-width: 76px !important;
        min-height: 76px !important;
        max-width: 76px !important;
        max-height: 76px !important;
        flex-basis: 76px !important;
        border-radius: 18px !important;
      }

      #loop-services-section .loop-category-carousel-v4-img {
        max-width: 76px !important;
        max-height: 76px !important;
      }

      #loop-services-section .loop-category-carousel-v4-label {
        max-width: 68px !important;
        font-size: 11.3px !important;
      }

      .loop-category-carousel-v4-mobile-controls {
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        gap: 7px !important;
        margin-top: 2px !important;
      }



      /* Mobile visual polish: less empty space below categories */
      #loop-services-section {
        padding-bottom: 16px !important;
      }

      #loop-services-section .loop-section-head {
        margin-bottom: 18px !important;
      }

      #loop-services-section .loop-section-card {
        padding-bottom: 0 !important;
      }

      .loop-category-carousel-v4-mobile-controls {
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
      }

      .loop-category-carousel-v4-dot {
        width: 6px !important;
        height: 6px !important;
        border-radius: 999px !important;
        border: 0 !important;
        padding: 0 !important;
        background: rgba(148, 163, 184, .45) !important;
      }

      .loop-category-carousel-v4-dot.is-active {
        width: 18px !important;
        background: #00897f !important;
      }
    }


    /* Desktop polish for MainMenu V2. Mobile logic remains unchanged. */
    @media (min-width: 900px) {
      #loop-services-section .loop-category-carousel-v4-root {
        width: 100% !important;
      }

      #loop-services-section .loop-category-carousel-v4-card {
        transition: transform 160ms ease, filter 160ms ease !important;
      }

      #loop-services-section .loop-category-carousel-v4-card:hover {
        transform: translateY(-5px) !important;
        filter: saturate(1.05) !important;
      }
    }

    /* LOOP Phase 3 category polish */
    @media (min-width: 900px) {
      #loop-services-section .loop-category-carousel-v4-card:hover {
        transform: translateY(-6px) !important;
      }
    }


    /* LOOP PHASE 6C: deterministic visible icons */
    #loop-services-section .loop-category-carousel-v4-image {
      background: transparent !important;
      box-shadow: none !important;
      overflow: visible !important;
    }

    #loop-services-section .loop-category-carousel-v4-img {
      display: block !important;
      opacity: 1 !important;
      visibility: visible !important;
      width: 100% !important;
      height: 100% !important;
      max-width: 100% !important;
      max-height: 100% !important;
      object-fit: contain !important;
    }


    /* PHASE 7C: single source category icons + no white frame.
       Images live in src/assets/images/categories.
       The tiny scale crops only any accidental outer canvas, not the icon design. */
    #loop-services-section .loop-category-carousel-v4-image,
    #loop-services-section .loop-direct-category-icon {
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      overflow: hidden !important;
    }

    #loop-services-section .loop-category-carousel-v4-img,
    #loop-services-section .loop-direct-category-icon img {
      background: transparent !important;
      object-fit: cover !important;
      transform: scale(1.035) !important;
      transform-origin: center center !important;
    }


    /* PHASE 7D mobile compact categories - compile safe */
    @media (max-width: 899px) {
      #loop-services-section .loop-category-carousel-v4 {
        transform: translateX(0) !important;
        gap: 13px !important;
      }

      #loop-services-section .loop-category-carousel-v4-card {
        flex: 0 0 60px !important;
        width: 60px !important;
        min-width: 60px !important;
        max-width: 60px !important;
        min-height: 78px !important;
        padding: 0 !important;
        gap: 6px !important;
        background: transparent !important;
        box-shadow: none !important;
      }

      #loop-services-section .loop-category-carousel-v4-image {
        width: 56px !important;
        height: 56px !important;
        min-width: 56px !important;
        min-height: 56px !important;
        max-width: 56px !important;
        max-height: 56px !important;
        border-radius: 18px !important;
        background: transparent !important;
        box-shadow: none !important;
      }

      #loop-services-section .loop-category-carousel-v4-img {
        width: 48px !important;
        height: 48px !important;
        max-width: 48px !important;
        max-height: 48px !important;
        object-fit: contain !important;
        transform: none !important;
        background: transparent !important;
      }

      #loop-services-section .loop-category-carousel-v4-label {
        width: 68px !important;
        max-width: 68px !important;
        font-size: 9.4px !important;
        line-height: 1.08 !important;
        font-weight: 950 !important;
      }

      .loop-category-carousel-v4-mobile-controls {
        display: none !important;
      }
    }


    /* PHASE 7F: keep mobile icon box size, shrink artwork so it is never cropped. */
    @media (max-width: 899px) {
      body.is-mobile-app #loop-services-section .loop-category-carousel-v4-image {
        width: 56px !important;
        height: 56px !important;
        min-width: 56px !important;
        min-height: 56px !important;
        max-width: 56px !important;
        max-height: 56px !important;
        border-radius: 18px !important;
        overflow: visible !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        background: transparent !important;
        box-shadow: none !important;
      }

      body.is-mobile-app #loop-services-section .loop-category-carousel-v4-img,
      body.is-mobile-app #loop-services-section .loop-category-carousel-v4-image img {
        width: 48px !important;
        height: 48px !important;
        max-width: 48px !important;
        max-height: 48px !important;
        object-fit: contain !important;
        object-position: center center !important;
        transform: none !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: block !important;
        background: transparent !important;
      }
    }


    /* PHASE 7G: mobile category carousel safety — no cropped/overlapped icons. */
    @media (max-width: 899px) {
      body.is-mobile-app #loop-services-section .loop-category-carousel-v4 {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        transform: none !important;
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: nowrap !important;
        justify-content: space-between !important;
        align-items: flex-start !important;
        gap: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        box-sizing: border-box !important;
      }

      body.is-mobile-app #loop-services-section .loop-category-carousel-v4-card {
        flex: 0 0 58px !important;
        width: 58px !important;
        min-width: 58px !important;
        max-width: 58px !important;
        min-height: 88px !important;
        padding: 0 !important;
        margin: 0 !important;
        gap: 6px !important;
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
        overflow: visible !important;
        box-sizing: border-box !important;
      }

      body.is-mobile-app #loop-services-section .loop-category-carousel-v4-image {
        width: 52px !important;
        height: 52px !important;
        min-width: 52px !important;
        min-height: 52px !important;
        max-width: 52px !important;
        max-height: 52px !important;
        flex: 0 0 52px !important;
        border-radius: 16px !important;
        margin: 0 auto !important;
        padding: 0 !important;
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
        overflow: hidden !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        box-sizing: border-box !important;
        isolation: isolate !important;
      }

      body.is-mobile-app #loop-services-section .loop-category-carousel-v4-img,
      body.is-mobile-app #loop-services-section .loop-category-carousel-v4-image img {
        position: static !important;
        width: 52px !important;
        height: 52px !important;
        min-width: 0 !important;
        min-height: 0 !important;
        max-width: 52px !important;
        max-height: 52px !important;
        flex: 0 0 auto !important;
        object-fit: contain !important;
        object-position: center center !important;
        border-radius: 16px !important;
        transform: none !important;
        background: transparent !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: block !important;
        box-sizing: border-box !important;
      }

      body.is-mobile-app #loop-services-section .loop-category-carousel-v4-label {
        width: 64px !important;
        max-width: 64px !important;
        margin: 0 auto !important;
        padding: 0 !important;
        font-size: 8.9px !important;
        line-height: 1.08 !important;
        font-weight: 950 !important;
        letter-spacing: -.05em !important;
        text-align: center !important;
        overflow: visible !important;
        white-space: normal !important;
        display: block !important;
      }
    }

  `;

  const renderItem = (it, idx) => {
    const label = it && (it.name || it.id) ? it.name || it.id : String(it);
    const resolved = tryFindImageResolved(it, idx) || PUBLIC_NO_IMAGE;

    let primary = resolved;
    if (isBrowserWeb() && typeof resolved === "string" && resolved.startsWith("/")) {
      try {
        primary = window.location.origin + resolved;
      } catch (e) {}
    }

    const candidates = buildRuntimeVariants(resolved);
    const ordered = Array.from(new Set([primary].concat(candidates)));
    const key = it && it.id ? it.id : `${normalizeName(label)}_${idx}`;

    return (
      <button
        key={key}
        type="button"
        className="loop-category-carousel-v4-card"
        onClick={() => onSelect && onSelect(it)}
        aria-label={`Categoría ${label}`}
      >
        <div className="loop-category-carousel-v4-image">
          <ImageWithFallback
            key={ordered[0]}
            src={ordered[0]}
            candidates={ordered.slice(1)}
            alt={label}
            className="loop-category-carousel-v4-img"
            fallback={PUBLIC_NO_IMAGE}
          />
        </div>

        <div className="loop-category-carousel-v4-label">{label}</div>
      </button>
    );
  };

  return (
    <div className="loop-category-carousel-v4-root">
      <style>{css}</style>

      <div
        className="loop-category-carousel-v4-mobile-window"
        onPointerDown={handleCarouselPointerDown}
        onPointerUp={handleCarouselPointerUp}
        onPointerCancel={handleCarouselPointerCancel}
        onPointerLeave={handleCarouselPointerCancel}
      >
        <div
          className="loop-category-carousel-v4"
          style={{ "--loop-mobile-slide-index": mobileSlideIndex }}
        >
          {toRender.map((it, idx) => renderItem(it, idx))}
        </div>
      </div>

      {effectiveMaxMobileSlideIndex > 0 ? (
        <div className="loop-category-carousel-v4-mobile-controls" aria-hidden="true">
          {Array.from({ length: effectiveMaxMobileSlideIndex + 1 }).map((_, idx) => (
            <button
              key={`cat-dot-${idx}`}
              type="button"
              className={`loop-category-carousel-v4-dot ${idx === mobileSlideIndex ? "is-active" : ""}`}
              onClick={() => setMobileSlideIndex(idx)}
              aria-label={`Ver grupo ${idx + 1}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// LOOP PHASE 5B: category icons are supplied in src/assets/images/Servicios.
